/**
 * Org-scoped News Feed (CVA-4718) — Redux slice
 *
 * Same page===1-replace vs page>1-append + list-of-posts pattern as the
 * Tenant's `redux/slices/classveew-admin/news-feed.slice.ts`, but backed by
 * `redux/action/news-feed.action.ts` (org-scoped `/v2/post/*`). Powers both
 * the Dashboard "Recent Activities" widget and the full News Feed page via
 * the shared `PaginatedPostList` component, so functional parity holds by
 * construction.
 *
 * Comment state (recent-comments preview, "view all" pagination) is
 * intentionally NOT duplicated here — `components/Post/CommentSection.tsx`
 * remains wired to the legacy `post` slice's `commentPagination` for that
 * (it is independent of which slice owns the post list). To keep this
 * slice's post cards in sync when a comment is added/removed from either
 * surface, this slice listens for the same plain actions CommentSection already
 * dispatches (`addPaginatedPostComment` / `removePaginatedPostComment` /
 * `updateRecentCommentsFromPaginated`) via `extraReducers` — no changes to
 * CommentSection were needed.
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import i18n from '../../i18n/i18n';
import { IPost } from '../../interface/postModel';
import { INewsFeedState } from '../../interface/news-feed/news-feed.interface';
import {
  fetchNewsFeedPostsPaginated,
  fetchMoreNewsFeedPosts,
  toggleNewsFeedPostLike,
  createNewsFeedPost,
  editNewsFeedPost,
  deleteNewsFeedPostThunk,
} from '../action/news-feed.action';
import {
  addPaginatedPostComment,
  removePaginatedPostComment,
  updateRecentCommentsFromPaginated,
} from './postSliceModel';

const initialState: INewsFeedState = {
  posts: [],
  pagination: null,
  isLoadingPaginated: false,
  isLoadingMore: false,
  error: null,
};

const newsFeedSlice = createSlice({
  name: 'newsFeed',
  initialState,
  reducers: {
    /** Clear feed (e.g. on unmount) so a fresh mount doesn't flash stale data. */
    clearNewsFeedPosts(state) {
      state.posts = [];
      state.pagination = null;
      state.error = null;
    },
    /** Optimistic like/unlike update for a single post. */
    updateNewsFeedPostLike(
      state,
      action: PayloadAction<{ postId: string; isLiked: boolean; likesCount: number }>,
    ) {
      const { postId, isLiked, likesCount } = action.payload;
      const post = state.posts.find((p) => p.id === postId);
      if (post) {
        post.isLiked = isLiked;
        post.likesCount = likesCount;
      }
    },
    /** Optimistic post removal (delete flow). */
    removeNewsFeedPost(state, action: PayloadAction<string>) {
      state.posts = state.posts.filter((p) => p.id !== action.payload);
      if (state.pagination) {
        state.pagination.total = Math.max(0, state.pagination.total - 1);
      }
    },
    /** Restore a post that was optimistically removed (delete failed). */
    restoreNewsFeedPost(state, action: PayloadAction<{ post: IPost; index: number }>) {
      const { post, index } = action.payload;
      state.posts.splice(index, 0, post);
      if (state.pagination) {
        state.pagination.total += 1;
      }
    },
  },
  extraReducers: (builder) => {
    // ── Fetch page 1 (replace) ──
    builder.addCase(fetchNewsFeedPostsPaginated.pending, (state) => {
      state.isLoadingPaginated = true;
      state.error = null;
    });
    builder.addCase(fetchNewsFeedPostsPaginated.fulfilled, (state, action) => {
      state.isLoadingPaginated = false;
      state.posts = action.payload.data.post;
      state.pagination = action.payload.data.pagination;
      state.error = null;
    });
    builder.addCase(fetchNewsFeedPostsPaginated.rejected, (state, action) => {
      state.isLoadingPaginated = false;
      state.error =
        action.payload?.message || i18n.t('toast.failed_to_fetch_posts', 'Failed to fetch posts');
    });

    // ── Fetch page > 1 (append) ──
    builder.addCase(fetchMoreNewsFeedPosts.pending, (state) => {
      state.isLoadingMore = true;
    });
    builder.addCase(fetchMoreNewsFeedPosts.fulfilled, (state, action) => {
      state.isLoadingMore = false;
      state.posts = [...state.posts, ...action.payload.data.post];
      state.pagination = action.payload.data.pagination;
    });
    builder.addCase(fetchMoreNewsFeedPosts.rejected, (state, action) => {
      state.isLoadingMore = false;
      state.error =
        action.payload?.message ||
        i18n.t('toast.failed_to_fetch_more_posts', 'Failed to fetch more posts');
    });

    // ── Like (error surfacing only — state updates are optimistic) ──
    builder.addCase(toggleNewsFeedPostLike.rejected, (state, action) => {
      state.error =
        action.payload?.message || i18n.t('toast.failed_to_toggle_like', 'Failed to toggle like');
    });

    // ── Create (prepend) ──
    builder.addCase(createNewsFeedPost.fulfilled, (state, action) => {
      if (action.payload?.data?.post) {
        state.posts = [action.payload.data.post, ...state.posts];
        if (state.pagination) {
          state.pagination.total += 1;
        }
      }
    });
    builder.addCase(createNewsFeedPost.rejected, (state, action) => {
      state.error =
        action.payload?.message || i18n.t('toast.failed_to_create_post', 'Failed to create post');
    });

    // ── Edit (replace matching post) ──
    builder.addCase(editNewsFeedPost.fulfilled, (state, action) => {
      const updatedPost = action.payload?.data?.post;
      if (updatedPost) {
        const index = state.posts.findIndex((p) => p.id === updatedPost.id);
        if (index !== -1) {
          state.posts[index] = updatedPost;
        }
      }
    });
    builder.addCase(editNewsFeedPost.rejected, (state, action) => {
      state.error =
        action.payload?.message || i18n.t('toast.failed_to_edit_post', 'Failed to edit post');
    });

    // ── Delete (error surfacing — optimistic remove/restore dispatched by the caller) ──
    builder.addCase(deleteNewsFeedPostThunk.rejected, (state, action) => {
      state.error =
        action.payload?.message || i18n.t('toast.failed_to_delete_post', 'Failed to delete post');
    });

    // ── Keep post cards in sync with comment changes made via the shared
    //    CommentSection component, which still targets the legacy `post`
    //    slice's actions for its own "recent comments" bookkeeping. ──
    builder.addCase(addPaginatedPostComment, (state, action) => {
      const { postId, comment } = action.payload;
      const post = state.posts.find((p) => p.id === postId);
      if (post) {
        const currentComments = post.recentComments || [];
        post.recentComments = [comment, ...currentComments].slice(0, 2);
        post.commentsCount += 1;
      }
    });
    builder.addCase(removePaginatedPostComment, (state, action) => {
      const { postId, commentId } = action.payload;
      const post = state.posts.find((p) => p.id === postId);
      if (post) {
        post.recentComments = (post.recentComments || []).filter((c) => c.id !== commentId);
        if (post.commentsCount > 0) {
          post.commentsCount -= 1;
        }
      }
    });
    builder.addCase(updateRecentCommentsFromPaginated, (state, action) => {
      const { postId, comments, total } = action.payload;
      const post = state.posts.find((p) => p.id === postId);
      if (post) {
        post.recentComments = comments.slice(0, 2);
        post.commentsCount = total;
      }
    });
  },
});

export const {
  clearNewsFeedPosts,
  updateNewsFeedPostLike,
  removeNewsFeedPost,
  restoreNewsFeedPost,
} = newsFeedSlice.actions;
export default newsFeedSlice.reducer;
